import { Switch } from "src/sudokit/components/Switch";
import PreflightLoaderButton from "../PreflightLoaderButton";
import CancelPreflightRunProseButtonGroup from "./CancelPreflightRunProseButtonGroup";
import RunPreflightSelectModelButton from "./RunPreflightSelectModelButton";
import ProseModeSelectorButton from "./ProseModeSelectorButton";
import DisabledGenerateProseButton from "./DisabledGenerateProseButton";
import CancelProseGenerationButton from "./CancelProseGenerationButton";
import ProseGeneratingPauseButton from "./ProseGeneratingPauseButton";
import PostGeneratedIdleButtonGroup from "./PostGeneratedIdleButtonGroup";
import { sendInvocationEvent } from "src/Utils";
import XStateConditionalRenderer from "src/components/XStateConditionalRenderer";
import { DraftStates } from "../draftStateMachine";
import { AppEventPublisher } from "src/AppEventPublisher";
import { TourSignal } from "../../../StickyTour/TourTypes";
import { useStickyTourStore } from "../../../../stores/stickyTourStore";

function PanelButtons({
  draftStateMachine,
  hasAccessToStyleGuide,
  highQuality,
  setHighQuality,
  requesting,
  outOfQuota,
  proseMode,
  PROSE_MODES,
  sceneId,
  setScenes,
  prosePreflight,
  documentScenes,
  scene,
  generateProse,
  setLastInvocation,
  editor,
  editorRef,
  abortStream,
  recordDraftPause,
  setRequesting,
  setPreflightResponse,
  lastInvocation,
  setProseWords,
  currentProseMode,
}) {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const isPreflightRunning = draftStateMachine.value === DraftStates.PREFLIGHT_RUNNING;

  return (
    <>
      {/* Prose Mode selector — visible in DEFAULT, PREFLIGHT_RUNNING, and POST_GENERATED_IDLE */}
      <XStateConditionalRenderer
        currentState={draftStateMachine.value}
        inclusiveStates={[DraftStates.DEFAULT, DraftStates.PREFLIGHT_RUNNING, DraftStates.POST_GENERATED_IDLE]}
      >
        {hasAccessToStyleGuide && !proseMode?.benchmarkMode && (
          <div className="shrink-0 mr-2 text-app-grey-1">
            <Switch
              label="High Quality"
              labelClassName="!text-sm whitespace-nowrap"
              checked={highQuality}
              onCheckedChange={setHighQuality}
              disabled={isPreflightRunning || requesting}
            />
          </div>
        )}
        <ProseModeSelectorButton
          proseMode={proseMode}
          PROSE_MODES={PROSE_MODES}
          sceneId={sceneId}
          setScenes={setScenes}
          scene={scene}
          disabled={isPreflightRunning}
        />
      </XStateConditionalRenderer>

      <XStateConditionalRenderer
        currentState={draftStateMachine.value}
        inclusiveStates={[DraftStates.PREFLIGHT_RUNNING]}
      >
        <PreflightLoaderButton />
      </XStateConditionalRenderer>

      <XStateConditionalRenderer
        currentState={draftStateMachine.value}
        inclusiveStates={[DraftStates.PREFLIGHT_REVIEW]}
      >
        <CancelPreflightRunProseButtonGroup
          requesting={requesting}
          outOfQuota={outOfQuota}
          setPreflightResponse={setPreflightResponse}
          draftStateMachine={draftStateMachine}
          generateProse={generateProse}
          setLastInvocation={setLastInvocation}
          editor={editor}
          editorRef={editorRef}
          scene={scene}
          setScenes={setScenes}
          sceneId={sceneId}
        />
      </XStateConditionalRenderer>

      <XStateConditionalRenderer
        currentState={draftStateMachine.value}
        inclusiveStates={[DraftStates.DEFAULT]}
      >
        <RunPreflightSelectModelButton
          requesting={requesting}
          outOfQuota={outOfQuota}
          prosePreflight={prosePreflight}
        />
      </XStateConditionalRenderer>

      <XStateConditionalRenderer
        currentState={draftStateMachine.value}
        inclusiveStates={[DraftStates.REWRITING_SCENES]}
      >
        <DisabledGenerateProseButton />
      </XStateConditionalRenderer>

      <XStateConditionalRenderer
        currentState={draftStateMachine.value}
        inclusiveStates={[DraftStates.WAITING_FOR_PROSE]}
      >
        <CancelProseGenerationButton
          onCancel={() => {
            AppEventPublisher.storyBible.draft.cancelProseGeneration();
            abortStream();
            setRequesting(false);
            setPreflightResponse(null);
            editor.setOptions({ editable: true });
            draftStateMachine.events.cancelProseGeneration();
          }}
        />
      </XStateConditionalRenderer>

      <XStateConditionalRenderer
        currentState={draftStateMachine.value}
        inclusiveStates={[DraftStates.GENERATING_PROSE]}
      >
        <ProseGeneratingPauseButton
          currentProseMode={currentProseMode}
          onPause={() => {
            if (recordDraftPause) recordDraftPause();
            else AppEventPublisher.storyBible.draft.pauseProseGeneration();
            abortStream();
            sendInvocationEvent(lastInvocation, "cancel");
            setProseWords(0);
            setScenes((draft) => {
              draft[sceneId].isContinue = true;
              draft[sceneId].restartFromIndex = draft[sceneId].lastCommandIndex;
            });
            editor.setOptions({ editable: true });
            sendFlowSignal(TourSignal.DRAFT_PROSE_GENERATE_GO_PAUSED);
            draftStateMachine.events.pause();
          }}
        />
      </XStateConditionalRenderer>

      <XStateConditionalRenderer
        currentState={draftStateMachine.value}
        inclusiveStates={[DraftStates.POST_GENERATED_IDLE]}
      >
        <PostGeneratedIdleButtonGroup
          requesting={requesting}
          outOfQuota={outOfQuota}
          sceneId={sceneId}
          setScenes={setScenes}
          prosePreflight={prosePreflight}
          documentScenes={documentScenes}
          scene={scene}
        />
      </XStateConditionalRenderer>
    </>
  );
}

export default PanelButtons;
